<template>
  <el-dialog custom-class="management-dialog" :visible="visible" :title="(editing ? '编辑' : '存为') + kindLabel" width="min(620px, calc(100vw - 24px))" append-to-body :close-on-click-modal="false" :close-on-press-escape="!saving" :show-close="!saving" :before-close="close">
    <div class="template-save-body">
      <template-preview v-if="snapshot" :value="snapshot" :height="150" />
      <details class="management-dialog__help"><summary>保存内容与使用方式</summary><p>{{ helpText }}</p></details>
      <p v-if="sourceHint" class="template-save-help">{{ sourceHint }}</p>
      <el-form ref="form" :model="form" :rules="rules" label-position="top" :disabled="saving">
        <el-form-item v-if="!editing && hasFixedDevices" label="固定设备绑定">
          <el-radio-group v-model="preserveFixedDevices">
            <el-radio :label="true">保留原设备</el-radio>
            <el-radio :label="false">使用时重新选择</el-radio>
          </el-radio-group>
          <p class="template-save-help">重新选择时保留产品、属性及查询配置，仅清除固定设备。</p>
        </el-form-item>
        <el-form-item :label="resourceMode ? '组件名称' : '模板名称'" prop="name"><el-input v-model="form.name" maxlength="128" show-word-limit /></el-form-item>
        <el-form-item label="归属类型 / 可见范围">
          <el-radio-group v-model="form.visibility"><el-radio label="private" :disabled="!!(editing && editing.isSystem)">私有 · 仅自己可见</el-radio><el-radio label="public" :disabled="!canPublish">公开 · 其他账号可查询和使用</el-radio></el-radio-group>
          <p v-if="editing && editing.isSystem" class="template-save-help">系统组件保持公开；需要私有样式时可创建副本。</p>
          <p v-if="!canPublish" class="template-save-help">公开{{ resourceMode ? '组件' : '模板' }}需要对应资源的公开权限。</p>
        </el-form-item>
        <el-form-item label="所属分类（可多选）" prop="catalogIds">
          <el-select popper-class="management-dialog-options" v-model="form.catalogIds" multiple filterable placeholder="选择分类" style="width: 100%" :loading="loadingCatalogs">
            <el-option v-for="item in options" :key="item.id" :label="item.label" :value="item.id" />
          </el-select>
        </el-form-item>
        <p v-if="catalogError" role="alert">{{ catalogError }} <el-button type="text" @click="loadCatalogs">重试</el-button></p>
        <p v-else-if="!loadingCatalogs && !options.length" role="status">请先在资源中心 → {{ resourceMode ? '组件' : '模板' }}中新增分类。</p>
        <el-form-item :label="resourceMode ? '组件说明' : '模板说明'"><el-input v-model="form.description" type="textarea" :rows="3" maxlength="1000" show-word-limit /></el-form-item>
      </el-form>
      <p v-if="error" role="alert" class="template-save-error">{{ error }}</p>
    </div>
    <span slot="footer"><el-button :disabled="saving" @click="close">{{ uncertain ? '关闭并检查列表' : '取消' }}</el-button><el-button type="primary" :loading="saving" :disabled="!canSave || loadingCatalogs || !!catalogError || uncertain" @click="save">{{ resourceMode ? '保存组件' : '保存模板' }}</el-button></span>
  </el-dialog>
</template>
<script>
import store from '@/store'
import * as templateApi from '@/api/templates'
import * as componentApi from '@/api/component-resources'
import { savedComponentResource, customComponentCatalogs } from '@/utils/component-resources'
import { saveResourceCover } from '@/utils/resource-cover'
import { templateCoverReadable } from '@/utils/template-cover'
import { templateCatalogs, flattenTemplateCatalogs, savedTemplate, templateError } from '@/utils/templates'
import { createRequestCancellation, isRequestCanceled } from '@/api/http'
import TemplatePreview from './preview.vue'
import { configureTemplateDevices, hasTemplateFixedDevices } from '@/editor/core/component-template'
export default {
  components: { TemplatePreview },
  props: { resourceMode: Boolean, visible: Boolean, snapshot: Object, editing: Object, defaultName: String, sourceHint: String, sourceCoverFileId: String },
  data: () => ({ form: { name: '', catalogIds: [], description: '', visibility: 'private' }, preserveFixedDevices: true, options: [], saving: false, uncertain: false, error: '', catalogError: '', loadingCatalogs: false,
    rules: { name: [{ required: true, whitespace: true, message: '请输入模板名称', trigger: 'blur' }], catalogIds: [{ type: 'array', required: true, min: 1, message: '请至少选择一个分类', trigger: 'change' }] } }),
  computed: {
    api () { return this.resourceMode ? componentApi : templateApi },
    permission () { return this.resourceMode ? 'visualizationComponent' : 'visualizationTemplate' },
    canPublish () { return store.getters['auth/hasResourcePermission'](this.permission + ':publish') },
    kindLabel () { if (this.resourceMode) return '组件'; return { page: '页面模板', project: '项目模板', component: '组件模板' }[(this.snapshot || {}).kind] || '模板' },
    helpText () {
      const imageHint = '明确绑定的图片保留固定资源版本，保存时校验文件；旧图片地址和背景图不自动清理。'
      if (this.snapshot && this.snapshot.kind === 'project') return '保存全部页面、首页、布局、内部跳转及静态、接口、WebSocket、脚本和设备数据配置。页面设备来源跟随进入的设备，编辑示例设备不保存。使用模板创建独立大屏。' + imageHint
      return (this.snapshot && this.snapshot.kind === 'page' ? '保存整页尺寸、背景、布局及静态、接口、WebSocket、脚本和设备数据配置。页面设备来源跟随进入的设备，编辑示例设备不保存。应用时替换当前页，可撤销；页面跳转需重新配置。' : '保存样式与组合布局。使用模板会生成独立副本，使用后请配置设备数据及页面跳转。') + imageHint
    },
    hasFixedDevices () { return this.snapshot && hasTemplateFixedDevices(this.snapshot) },
    canSave () { return store.getters['auth/hasResourcePermission'](this.permission + ':save') && store.getters['auth/hasResourcePermission'](this.permission + ':query') }
  },
  watch: {
    visible (value) { if (value) this.open(); else this.cancelRead() },
    canSave (value) { if (!value) { this.cancelRead(); this.$emit('update:visible', false) } }
  },
  created () { this._read = 0; if (this.visible) this.open() },
  beforeDestroy () { this.cancelRead() },
  methods: {
    open () {
      this.preserveFixedDevices = true
      this.form = this.editing ? { name: this.editing.name, description: this.editing.description, catalogIds: this.editing.catalogIds.slice(), visibility: this.editing.visibility || 'private' } : { name: this.defaultName || '', catalogIds: [], description: '', visibility: 'private' }
      this.error = ''; this.uncertain = false
      this.$nextTick(() => { if (this.$refs.form) this.$refs.form.clearValidate() })
      this.loadCatalogs()
    },
    cancelRead () { this._read++; if (this._cancel) this._cancel.cancel() },
    async loadCatalogs () {
      this.cancelRead(); const request = this._read
      if (!this.canSave || !this.visible) return
      this._cancel = createRequestCancellation(); this.loadingCatalogs = true; this.catalogError = ''
      try {
        const tree = templateCatalogs(await this.api.getTemplateCatalogs(this.snapshot.kind, { cancelToken: this._cancel.token }))
        if (request !== this._read || this._isDestroyed) return
        // 已有资源仍能保留原登记分类；新建副本必须选择自建分类。
        this.options = flattenTemplateCatalogs(this.resourceMode && !(this.editing && this.editing.catalogIds.includes('res_component_default')) ? customComponentCatalogs(tree) : tree)
      } catch (error) { if (request === this._read && !isRequestCanceled(error)) this.catalogError = templateError(error) }
      finally { if (request === this._read) this.loadingCatalogs = false }
    },
    close () { if (!this.saving) this.$emit('update:visible', false) },
    async save () {
      if (this.saving || this.uncertain || !this.canSave || this.catalogError || this.loadingCatalogs) return
      let valid = false
      await this.$refs.form.validate(ok => { valid = ok })
      if (!valid || !this.canSave || this.saving || this.uncertain) return
      if (this.form.catalogIds.some(id => !this.options.some(item => item.id === id))) { this.error = '所选分类已不可用，请重新选择'; return }
      this.saving = true; this.error = ''
      const previous = this.editing
      if (previous && previous.isSystem && this.form.visibility !== 'public') { this.error = '系统组件保持公开，私有样式请另存副本'; this.saving = false; return }
      if (this.form.visibility === 'public' && !this.canPublish) { this.error = '当前账号没有公开模板权限，请选择私有'; this.saving = false; return }
      const payload = { name: this.form.name.trim(), description: this.form.description.trim(), catalogIds: this.form.catalogIds.slice(), visibility: this.form.visibility || 'private' }
      if (previous) payload.revision = previous.revision
      else {
        try { payload.snapshot = JSON.stringify(configureTemplateDevices(this.snapshot, this.preserveFixedDevices)) }
        catch (error) { this.error = error.message; this.saving = false; return }
      }
      let submitted = false, preparingCover = false
      try {
        if (!previous && this.snapshot.kind === 'project') {
          if (this.sourceCoverFileId && await templateCoverReadable(this.sourceCoverFileId)) payload.coverFileId = this.sourceCoverFileId
          if (this._isDestroyed || !this.visible || !this.canSave) return
        }
        if (!previous && !payload.coverFileId) {
          preparingCover = true
          payload.coverFileId = await saveResourceCover(JSON.parse(payload.snapshot), () => !this._isDestroyed && this.visible && this.canSave)
          preparingCover = false
        }
        if (this._isDestroyed || !this.visible || !this.canSave) return
        if (payload.visibility === 'public' && !this.canPublish) throw new Error('公开权限已变更，本次未提交')
        submitted = true
        const response = previous ? await this.api.updateTemplate(previous.id, payload) : await this.api.createTemplate(payload)
        this.uncertain = true
        const item = (this.resourceMode ? savedComponentResource : savedTemplate)(response, previous, payload.coverFileId)
        if (!previous && item.kind !== this.snapshot.kind) throw new Error('保存返回的模板类型不一致')
        if (this._isDestroyed || !this.canSave) return
        this.uncertain = false; this.$emit('saved', item); this.$emit('update:visible', false)
      } catch (error) {
        if (!this._isDestroyed) {
          if (submitted && (!error.status || error.status >= 500)) this.uncertain = true
          this.error = this.uncertain ? '保存结果未确认，请关闭后刷新列表检查，勿重复提交' : (preparingCover ? '模板封面生成或上传失败：' : '') + templateError(error)
        }
      } finally { if (!this._isDestroyed) this.saving = false }
    }
  }
}
</script>
<style scoped>
.template-save-help { color: #64748b; font-size: 13px; line-height: 1.7; margin: 12px 0; }
.template-save-error { color: #b42318; overflow-wrap: anywhere; }
</style>
